/**
 * ExtractedDataModal Component
 * 
 * Modal to display extracted and normalized data after analysis.
 * Shows the data in a table format with CSV export functionality.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Table } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface ExtractedDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  revisionId: string;
  moduleId: string;
}

interface ExtractedRow {
  [key: string]: string | number | null;
}

export function ExtractedDataModal({
  isOpen,
  onClose,
  revisionId,
  moduleId,
}: ExtractedDataModalProps) {
  const [rows, setRows] = useState<ExtractedRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sourceInfo, setSourceInfo] = useState<{ filename?: string; rowCount: number }>({ rowCount: 0 });

  useEffect(() => {
    if (isOpen && revisionId) {
      fetchExtractedData();
    }
  }, [isOpen, revisionId]);

  const fetchExtractedData = async () => {
    setIsLoading(true);
    try {
      // Get data from batches (data_sample contains the normalized rows)
      const { data: batches } = await supabase
        .from('batches')
        .select('data_sample, result_json')
        .eq('revision_id', revisionId)
        .order('batch_no', { ascending: true });

      if (batches && batches.length > 0) {
        let allRows: ExtractedRow[] = [];
        
        for (const batch of batches) {
          // Check data_sample - it may contain rows directly or nested
          if (batch.data_sample && typeof batch.data_sample === 'object') {
            const sample = batch.data_sample as any;
            
            // Try various possible structures
            if (Array.isArray(sample)) {
              allRows = allRows.concat(sample);
            } else if (sample.normalized_rows && Array.isArray(sample.normalized_rows)) {
              allRows = allRows.concat(sample.normalized_rows);
            } else if (sample.rows && Array.isArray(sample.rows)) {
              allRows = allRows.concat(sample.rows);
            }
          }
        }

        if (allRows.length > 0) {
          setRows(allRows);
          setHeaders(Object.keys(allRows[0]).filter(h => !h.startsWith('_')));
          setSourceInfo({ rowCount: allRows.length });
        }
      }
    } catch (error) {
      console.error('[ExtractedDataModal] Error fetching data:', error);
      toast({
        title: 'Kunde inte hämta data',
        description: 'Ett fel uppstod vid hämtning av extraherad data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (rows.length === 0) return;

    // Build CSV content
    const csvLines: string[] = [];
    
    // Header row
    csvLines.push(headers.map(h => `"${h}"`).join(';'));
    
    // Data rows
    for (const row of rows) {
      const values = headers.map(h => {
        const val = row[h];
        if (val === null || val === undefined) return '';
        if (typeof val === 'number') return val.toString();
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvLines.push(values.join(';'));
    }

    // Create and download file
    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `extraherad-data-${revisionId.slice(0, 8)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export klar',
      description: `${rows.length} rader exporterades till CSV`,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Table className="h-5 w-5" />
            Extraherad data
          </DialogTitle>
          <DialogDescription>
            {sourceInfo.filename && (
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {sourceInfo.filename}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mb-4">
          <Badge variant="secondary" className="gap-1">
            {sourceInfo.rowCount} rader extraherade
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={rows.length === 0}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Exportera CSV
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Table className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Ingen extraherad data tillgänglig</p>
            <p className="text-sm mt-1">Data kanske inte har sparats för denna analys</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] border rounded-md">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground border-b">
                      #
                    </th>
                    {headers.map((header) => (
                      <th
                        key={header}
                        className="px-3 py-2 text-left font-medium text-muted-foreground border-b whitespace-nowrap"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/30'}
                    >
                      <td className="px-3 py-2 text-muted-foreground border-b">
                        {idx + 1}
                      </td>
                      {headers.map((header) => (
                        <td
                          key={header}
                          className="px-3 py-2 border-b whitespace-nowrap"
                        >
                          {row[header] !== null && row[header] !== undefined
                            ? typeof row[header] === 'number'
                              ? (row[header] as number).toLocaleString('sv-SE')
                              : String(row[header])
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
